import {
  isNotDefined,
  type AccountId,
  type AccountListInput,
  type AccountRollbackInput,
  type AccountStatusListInput,
  type ArchivedFilter,
  type AreaId,
  type AreaListInput,
  type AttachmentId,
  type CalendarAvailabilityInput,
  type CalendarClosersInput,
  type CalendarEventId,
  type CalendarEventListInput,
  type CalendarShadowInput,
  type CalendarSyncListInput,
  type CalendarTimeslotsInput,
  type ClientVersionInput,
  type CompanyId,
  type CompanyListInput,
  type CompliancePolicyId,
  type CustomLocationId,
  type DashboardId,
  type DashboardListInput,
  type DispositionId,
  type DispositionListInput,
  type EntityId,
  type EvaluationListInput,
  type ExportDownloadInput,
  type ExportListInput,
  type ExternalLinkId,
  type ExternalLinkListInput,
  type FeatureGetInput,
  type FeatureHistoryInput,
  type FeatureId,
  type GameId,
  type GameListInput,
  type Geo200Id,
  type ImportId,
  type ImportListInput,
  type InterviewId,
  type InterviewQuestionId,
  type InterviewQuestionListInput,
  type LatLng,
  type LocationActivityInput,
  type LocationAutocompleteProvider,
  type LocationId,
  type LocationLastActivityFilter,
  type LocationMapInput,
  type LocationPlaceId,
  type LocationPrescreenInput,
  type LocationScorecardInput,
  type LocationWirecenterOverlayInput,
  type MeetingHistoryInput,
  type MeetingListInput,
  type MeetingId,
  type MeetingOccurrenceListInput,
  type MessageEntityId,
  type PermissionCheckInput,
  type PermissionEntityId,
  type PlanKey,
  type PlanListInput,
  type Preference,
  type PrizeId,
  type QuestionId,
  type QuestionListInput,
  type QuestionResultListInput,
  type QuizId,
  type QuizListInput,
  type RatioId,
  type RatioListInput,
  type RecordingId,
  type RecordingRubricListInput,
  type ReportColumnInput,
  type ReportKpiInput,
  type ReportMapInput,
  type ReportTimeInput,
  type RoleGetInput,
  type RoleId,
  type RoleListInput,
  type RubricId,
  type SnapshotEmployeesInput,
  type TeamId,
  type TeamListInput,
  type UnsavedImport,
  type UserEmailListInput,
  type UserId,
  type UserIdentifier,
  type UserListInput,
  type UserSearchInput,
  type VideoId,
  type VideoListInput,
  type VideoSearchInput,
  type VideoTagsInput,
  type WebhookHistoryInput,
  type WorkflowId,
} from '@terros/common'
import { type QueryKey } from '@tanstack/react-query'

export const accountKey = (accountId: AccountId): QueryKey => ['account', accountId]
export const accountRollbackPreviewKey = (input: AccountRollbackInput): QueryKey => ['account-rollback', input]
export const accountsKey = (props: AccountListInput): QueryKey => ['accounts', props]
export const accountsInfiniteKey = (props: AccountListInput): QueryKey => ['accounts-infinite', props]
export const accountStatusesKey = (input?: AccountStatusListInput): QueryKey => {
  if (!input) return ['account-statuses']
  return ['account-statuses', input]
}
export const apiKeyKey = (): QueryKey => ['api-keys']
export const areaCanvassKey = (): QueryKey => ['areas-canvass']
export const areaGetKey = (areaId: AreaId | undefined): QueryKey => ['area', areaId]
export const areaListInfiniteKey = (props?: AreaListInput): QueryKey => {
  if (!props) return ['areas-infinite']
  return ['areas-infinite', props]
}
export const areaCountKey = (props?: AreaListInput): QueryKey => {
  if (!props) return ['areas-count']
  return ['areas-count', props]
}
export const attachmentKey = (attachmentId: AttachmentId): QueryKey => ['attachment', attachmentId]
export const autocompleteKey = (query: string, provider?: LocationAutocompleteProvider): QueryKey => [
  'autocomplete',
  query,
  provider,
]
export const calendarAvailabilityKey = (input: CalendarAvailabilityInput): QueryKey => ['calendar-availability', input]
export const calendarClosersKey = (input: CalendarClosersInput): QueryKey => ['calendar-closers', input]
export const calendarSyncListKey = (input: CalendarSyncListInput): QueryKey => ['calendar-sync-list', input]
export const chatGetKey = (sessionId: string): QueryKey => ['chat', sessionId]
export const chatListKey = (): QueryKey => ['chat-list']
export const companyKey = (companyId: CompanyId | undefined): QueryKey => ['company', companyId]
export const companyListKey = (props?: CompanyListInput): QueryKey => {
  if (!props) return ['companies']
  return ['companies', props]
}

export const dashboardKey = (dashboardId: DashboardId): QueryKey => ['dashboard', dashboardId]
export const dashboardsKey = (input?: DashboardListInput): QueryKey => {
  if (!input) return ['dashboards']
  return ['dashboards', input]
}
export const dispositionKey = (dispositionId?: DispositionId): QueryKey => ['disposition', dispositionId]
export const dispositionsKey = (parentOnly?: boolean, input?: DispositionListInput): QueryKey => {
  if (isNotDefined(parentOnly)) return ['dispositions']
  if (!input) return ['dispositions', parentOnly]
  return ['dispositions', parentOnly, input]
}
export const emailListKey = (props: UserEmailListInput): QueryKey => ['emails', props]
export const evaluationKey = (evaluationId: string): QueryKey => ['evaluation', evaluationId]
export const evaluationListKey = (props: EvaluationListInput): QueryKey => ['evaluations', props]
export const evaluationQuestionsKey = (): QueryKey => ['evaluationQuestions']
export const eventKey = (eventId?: CalendarEventId): QueryKey => {
  if (!eventId) return ['eventId']
  return ['eventId', eventId]
}
export const eventOutcomesKey = (): QueryKey => ['event-outcomes']
export const eventsKey = (props: CalendarEventListInput): QueryKey => ['calendar-events', props]
export const eventsInfiniteKey = (props: CalendarEventListInput): QueryKey => ['calendar-events-infinite', props]
export const exportsKey = (props: ExportListInput): QueryKey => ['exports', props]
export const exportDownloadKey = (props: ExportDownloadInput): QueryKey => ['export-download', props]

export const externalLinkKey = (externalLinkId: ExternalLinkId): QueryKey => ['external-link', externalLinkId]
export const externalLinkListKey = (props?: ExternalLinkListInput): QueryKey => {
  if (!props) return ['external-links']
  return ['external-links', props]
}

export const featureKey = (featureId: FeatureId | undefined): QueryKey => ['featureId', featureId]
export const featureGetKey = (props: FeatureGetInput): QueryKey => ['featureGet', props]
export const featureHistoryKey = (props: FeatureHistoryInput): QueryKey => ['featureHistory', props]
export const featureListKey = (): QueryKey => ['features']
export const gameKey = (gameId: GameId): QueryKey => ['game', gameId]
export const gameListKey = (input?: GameListInput): QueryKey => {
  if (!input) return ['games']
  return ['games', input]
}
export const gamePrizeDownloadKey = (url: string): QueryKey => ['game-prize-download', url]
export const gamePrizeInfoKey = (url: string): QueryKey => ['game-prize-info', url]
export const gamePrizeUploadKey = (gameId: GameId, prizeId?: PrizeId): QueryKey => {
  if (!prizeId) return ['game-prize-upload', gameId]
  return ['game-prize-upload', gameId, prizeId]
}
export const gameScorableKey = (): QueryKey => ['game-scorable']
export const importErrorsKey = (importId: ImportId): QueryKey => ['import-errors', importId]
export const importHeadersKey = (importData: Pick<UnsavedImport, 'entity' | 'workflowId'>): QueryKey => [
  'import-headers',
  importData.entity,
  importData.workflowId ?? null,
]
export const importKey = (importId: ImportId): QueryKey => ['import', importId]
export const importsKey = (props: ImportListInput): QueryKey => ['imports', props]
export const interviewKey = (interviewId: InterviewId): QueryKey => ['interview', interviewId]
export const interviewReportKey = (): QueryKey => ['interview-report']
export const interviewQuestionsListKey = (props?: InterviewQuestionListInput): QueryKey => {
  if (!props) return ['interview-questions']
  return ['interview-questions', props]
}
export const interviewQuestionKey = (interviewQuestionId: InterviewQuestionId): QueryKey => [
  'interview-question',
  interviewQuestionId,
]
export const locationAuditKey = (placeId: LocationPlaceId): QueryKey => ['location-audit', placeId]
/**
 * Defaults to extended, like /location/get. The basic key is a prefix of the extended one,
 * so invalidating `locationKey(id, false)` clears both.
 */
export const locationKey = (locationId: LocationId | undefined, extended = true): QueryKey => {
  if (!locationId) return ['location', 'NO_LOCATION_PROVIDED']
  if (!extended) return ['location', locationId]
  return ['location', locationId, extended]
}
export const customLocationRemoveCheckKey = (locationId: CustomLocationId): QueryKey => {
  return ['custom-location-remove-check', locationId]
}
export const customLocationMatchKey = (locationId: CustomLocationId): QueryKey => {
  return ['custom-location-match', locationId]
}
export const customLocationsKey: QueryKey = ['custom-locations']
export const locationTransferPreviewKey = (from: LocationId, to: LocationId): QueryKey => {
  return ['location-transfer-preview', from, to]
}
export const locationLastActivityKey = (filter?: LocationLastActivityFilter): QueryKey => {
  if (!filter) return ['location-last-activity']
  return ['location-last-activity', filter]
}
export const locationCountKey = (coordinates?: LatLng[]): QueryKey => {
  if (!coordinates) return ['location-count']
  return ['location-count', coordinates]
}
export const locationPrescreenKey = (input: LocationPrescreenInput): QueryKey => {
  const prefix = 'prescreen'
  if (input.residentData) {
    return [prefix, input.locationId, 'residentData', input.residentData.firstName, input.residentData.lastName]
  }
  throw new Error('Unexpected prescreen without residentData')
}
export const locationMapAttomFilterKey = ({ top, bottom, filter }: LocationMapInput): QueryKey => {
  return ['location-map-attom-filter', filter, top, bottom]
}
export const locationMapActivityFilterKey = ({ top, bottom, filter }: LocationActivityInput): QueryKey => {
  return ['location-map-activity-filter', filter, top, bottom]
}
export const locationsKey = (coordinates: LatLng): QueryKey => ['locations', coordinates]
export const locationLoadsKey = (): QueryKey => ['location-load']
export const locationLoadKey = (geo200Id: Geo200Id): QueryKey => [...locationLoadsKey(), geo200Id]
export const locationScorecardKey = (input: LocationScorecardInput): QueryKey => ['location-scorecard', input]
export const attendanceHistoryKey = (input: MeetingHistoryInput | undefined): QueryKey => {
  if (!input) return ['meeting-attendance-history']
  return ['meeting-attendance-history', input]
}
export const messageUnreadsKey = (): QueryKey => ['message-unreads']
export const oauthListKey = (): QueryKey => ['oauth-list']
export const planKey = (version: number, key: PlanKey, createIfMissing?: boolean): QueryKey => {
  if (!createIfMissing) {
    return ['plan', version, key]
  }
  return ['plan', version, key, createIfMissing]
}
export const plansKey = (input?: PlanListInput): QueryKey => {
  if (!input) return ['plans']
  const { scopeId, ...rest } = input
  return ['plans', scopeId, rest]
}
export const preferenceKey = (preference: Preference, targetId?: EntityId): QueryKey => {
  if (!targetId) return ['preference', preference]
  return ['preference', preference, targetId]
}
export const profileKey = (userId?: UserId): QueryKey => {
  if (!userId) return ['profile']
  return ['profile', userId]
}
export const uploadUrlsKey = (endpoint: string, entityId: string | undefined): QueryKey => [
  'upload-urls',
  endpoint,
  entityId,
]
export const userSessionListKey = (userId: UserId): QueryKey => ['user-session-list', userId]
export const questionKey = (questionId: QuestionId): QueryKey => ['question', questionId]
export const questionsKey = (input?: QuestionListInput): QueryKey => {
  if (!input) return ['questions']
  return ['questions', input]
}
export const questionCheckKey = (): QueryKey => ['questions-check']
export const questionResultsKey = (props: QuestionResultListInput): QueryKey => ['question-results', props]
export const quizKey = (quizId: QuizId): QueryKey => ['quiz', quizId]
export const quizListKey = (input?: QuizListInput): QueryKey => {
  if (!input) return ['quizzes']
  return ['quizzes', input]
}
export const quizTakeKey = (quizId: QuizId): QueryKey => ['quiz-take', quizId]
export const reverseGeocodeKey = (location: LatLng): QueryKey => ['reverse-geocode', location]
export const rubricActiveKey = (): QueryKey => ['rubric-active']
export const rubricKey = (rubricId: RubricId | undefined): QueryKey => {
  if (rubricId === undefined) return ['rubric', 'NO_RUBRIC_DEFINED']
  return ['rubric', rubricId]
}
export const rubricListKey = (input?: RecordingRubricListInput): QueryKey => {
  if (!input) return ['rubrics']
  return ['rubrics', input]
}
export const statsKey = (): QueryKey => ['stats']
export const intercomKey = (platform: string): QueryKey => ['intercom', platform]
export const userKey = (userId?: UserId): QueryKey => {
  if (!userId) return ['user']
  return ['user', userId]
}
export const userMetadataKey = (): QueryKey => ['user-metadata']
export const userLoginsKey = (userId?: UserId): QueryKey => {
  if (!userId) return ['user-logins']
  return ['user-logins', userId]
}
export const userLoginsInfiniteKey = (userId?: UserId): QueryKey => {
  if (!userId) return ['user-logins-infinite']
  return ['user-logins-infinite', userId]
}
export const userCheckKey = (): QueryKey => ['user-check']
export const userListKey = (props?: UserListInput): QueryKey => {
  if (!props) return ['users']
  return ['users', props]
}
export const userManagerKey = (userId?: UserId): QueryKey => {
  if (!userId) return ['user-manager']
  return ['user-manager', userId]
}
export const tinyUserKey = (userId?: UserIdentifier): QueryKey => {
  if (!userId) return ['tiny-user']
  return ['tiny-user', userId]
}
export const taskSnapshotEmployeesKey = (props: SnapshotEmployeesInput): QueryKey => ['task-snapshot-employees', props]
export const taskSnapshotManagersKey = (): QueryKey => ['task-snapshot-managers']
export const teamKey = (teamId?: TeamId): QueryKey => {
  if (!teamId) return ['team']
  return ['team', teamId]
}
export const teamDownlineKey = (entityId: TeamId | CompanyId): QueryKey => ['team-downline', entityId]
export const tinyTeamKey = (teamId: TeamId): QueryKey => ['tiny-team', teamId]
export const teamListKey = (props?: TeamListInput): QueryKey => {
  if (!props) return ['teams']
  return ['teams', props]
}

export const timeslotsKey = (props: CalendarTimeslotsInput): QueryKey => ['timeslots', props]
export const threadKey = (targetId: MessageEntityId): QueryKey => ['thread', targetId]
export const threadListKey = (): QueryKey => ['threads']
export const userSearchKey = (props: UserSearchInput): QueryKey => ['user-search', props]
export const ratioKey = (ratioId: RatioId): QueryKey => ['ratio', ratioId]
export const ratioListKey = (props?: RatioListInput): QueryKey => {
  if (!props) return ['ratios']
  return ['ratios', props]
}
export const rankListKey = (userId?: UserId): QueryKey => {
  if (!userId) return ['rank']
  return ['rank', userId]
}
export const recordingListKey = (userId: UserId): QueryKey => ['recording-list', userId]
export const recordingPlaybackKey = (recordingId: RecordingId): QueryKey => ['recording-playback', recordingId]
export const reportColumnKey = (input?: ReportColumnInput): QueryKey => {
  if (!input) return ['report-column']
  return ['report-column', input]
}
export const reportLeaderboardColumnsKey = (workflowId?: WorkflowId): QueryKey => {
  if (!workflowId) return ['report-leaderboard-columns']
  return ['report-leaderboard-columns', workflowId]
}
export const reportKpiKey = (props: ReportKpiInput): QueryKey => {
  props.kpis.sort()
  return ['report-kpi', props]
}
export const reportTimeKey = (props: ReportTimeInput): QueryKey => ['report-time', props]
export const reportMapKey = (props: ReportMapInput): QueryKey => ['report-map', props]
export const reportConfigListKey = (configType?: string): QueryKey => {
  if (!configType) return ['report-configs']
  return ['report-configs', configType]
}
export const reportUserKpiKey = (): QueryKey => ['report-user-kpi']
export const roleKey = (roleId: RoleId, props?: Partial<Omit<RoleGetInput, 'roleId'>>): QueryKey => {
  if (!props) return ['role', roleId]
  return ['role', roleId, props]
}
export const roleListKey = (props: RoleListInput): QueryKey => ['roles', props]
export const shadowAvailabilityKey = (props: CalendarShadowInput): QueryKey => ['shadow-availability', props]
export const tileKeyKey = (): QueryKey => ['tile-key']
export const versionKey = (props: ClientVersionInput): QueryKey => ['version', props]
export const videoKey = (videoId: VideoId): QueryKey => ['video', videoId]
export const videoListKey = (props: VideoListInput): QueryKey => ['videos', props]
export const videoNextKey = (): QueryKey => ['next-videos']
export const videoReportsKey = (limit?: number): QueryKey => {
  if (!limit) return ['video-reports']
  return ['video-reports', limit]
}
export const videoReportReasonsKey = (videoId: VideoId): QueryKey => ['video-report-reasons', videoId]
export const videoSearchKey = (props: VideoSearchInput): QueryKey => ['video-search', props]
export const videoTagsKey = (props: VideoTagsInput): QueryKey => ['video-tags', props]
export const videoUserBookmarkKey = (): QueryKey => ['video-user-bookmark']
export const videoProfileKey = (userId: UserId): QueryKey => ['video-profile', userId]
export const webhooksKey = (): QueryKey => ['webhooks']
export const webhookHistoryKey = (input: WebhookHistoryInput): QueryKey => ['webhook-history', input]
export const wirecenterKey = (input: LocationWirecenterOverlayInput): QueryKey => ['wirecenter', input]
export const permissionsKey = (targetId?: PermissionEntityId): QueryKey => {
  if (isNotDefined(targetId)) return ['permissions']
  return ['permissions', targetId]
}
export const permissionCheckKey = (input: PermissionCheckInput): QueryKey => [
  'permission-check',
  input.permissionType,
  input.targetId,
  input.match ?? 'any',
]

export const compliancePolicyKey = (policyId: string): QueryKey => ['compliance-policy', policyId]
export const compliancePoliciesKey = (archived?: ArchivedFilter): QueryKey =>
  archived ? ['compliance-policies', archived] : ['compliance-policies']

export const complianceViolationKey = (violationId: string): QueryKey => ['compliance-violation', violationId]
export const complianceViolationsKey = (entityId?: CompliancePolicyId | UserId): QueryKey =>
  entityId ? ['compliance-violations', entityId] : ['compliance-violations']

export const meetingListKey = (input: MeetingListInput): QueryKey => ['meetings', 'list', input]
export const meetingKey = (meetingId: MeetingId): QueryKey => ['meetings', 'get', meetingId]
export const meetingOccurrencesKey = (input: MeetingOccurrenceListInput): QueryKey => ['meetings', 'occurrences', input]
